import React, { useContext, useMemo, useState } from "react";
import { useLocation, useParams, useNavigate, Link } from "react-router";
import { Button, Stack } from "@mui/material";
import { string, bool } from "prop-types";
import type { GridRenderCellParams } from "@mui/x-data-grid";
import { useTextDomainContext, useUser } from "@/app/providers";
import {
  ActionMenu,
  BackButton,
  ConfirmNotification,
  TableView1,
} from "@/components";
import {
  useGetGroups,
  useHasPermission,
  usePageTitle,
  useRemoveCompanyFromTenant,
  useDeleteCompany,
} from "@/hooks";
import {
  permissionsLocationsReadonly,
  permissionsCompaniesFull,
} from "@/utils";
import {
  CompanyRow,
  ConfirmResult,
  SubsidiaryAction,
  SubsidiaryListProps,
} from "./types";

const feature = "common";

export const SubsidiaryList = (props: SubsidiaryListProps) => {
  //#region Permissions
  const { allow: allowLocationsReadonly } = useHasPermission(
    permissionsLocationsReadonly,
    feature,
  );
  const { allow: allowCompaniesFull } = useHasPermission(
    permissionsCompaniesFull,
    feature,
  );

  //#endregion Permissions

  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();
  const { tenantId: parmsTenantId = "", customerId: paramsCustomerId = "" } =
    useParams();

  const parentPath = props.parentPath || "system-admin";

  usePageTitle(gettext("Child Companies"));

  const tenantId = parmsTenantId || user?.currentTenantId || "";
  const customerId = props.customerId || paramsCustomerId || "";

  const showItems = useMemo(() => {
    return {
      showAdd: (props.showAdd && allowCompaniesFull) ?? false,
      showEdit: (props.showEdit && allowCompaniesFull) ?? false,
      showDelete:
        (props.showDelete && allowCompaniesFull) || allowCompaniesFull,
      showUnlink: false,
      showLocations: (props.showLocations && allowLocationsReadonly) ?? false,
    };
  }, [
    props.showAdd,
    props.showDelete,
    props.showEdit,
    props.showLocations,
    allowCompaniesFull,
    allowLocationsReadonly,
  ]);

  const { subsidiary: subsidiaries, loading } = useGetGroups({
    customerId,
  });

  const { removeCompanyFromTenant } = useRemoveCompanyFromTenant();
  const { deleteCompany } = useDeleteCompany();

  const [showConfirm, setShowConfirm] = useState(false);
  const [dialogParameters, setDialogParameters] = useState({});
  const [confirmMessage, setConfirmMessage] = useState("");

  const backLink =
    location.state?.parentPath ||
    (tenantId
      ? `/${parentPath}/tenants/${tenantId}/companies`
      : `/${parentPath}/companies`);

  const columns = [
    {
      field: "id",
      headerName: gettext("ID"),
      checkboxSelection: true,
    },
    {
      field: "name",
      headerName: gettext("Name"),
      headerAlign: "left",
      type: "string",
      flex: 1,
    },
    {
      field: "cvr",
      headerName: gettext("Company Number"),
      headerAlign: "left",
      type: "string",
      minWidth: 150,
    },
    {
      field: "customerType",
      headerName: gettext("Parent / Child"),
      headerAlign: "left",
      type: "string",
      minWidth: 150,
    },
    {
      field: "city",
      headerName: gettext("City"),
      headerAlign: "left",
      type: "string",
      minWidth: 150,
    },
    {
      field: "country",
      headerName: gettext("Country"),
      headerAlign: "left",
      type: "string",
      minWidth: 75,
    },
    {
      field: "actions",
      type: "actions",
      headerName: gettext("Actions"),
      hideable: false,
      maxWidth: 75,
      align: "right",
      headerAlign: "right",
      renderCell: (params: GridRenderCellParams<CompanyRow>) => (
        <ActionMenu
          menuFor="company"
          row={params.row}
          menuClick={menuClick}
          showItems={showItems}
        />
      ),
    },
  ];

  const initialState = {
    pagination: {
      paginationModel: {
        page: 0,
        pageSize: 100,
      },
    },
    columns: {
      columnVisibilityModel: {
        id: false,
      },
    },
  };

  const handleOnClickRemoveLink = (action: SubsidiaryAction, id?: string) => {
    setDialogParameters({
      action,
      id,
    });
    setShowConfirm(true);
  };

  const handleGetResult = (obj: ConfirmResult) => {
    if (obj.result === "yes") {
      switch (obj?.parameters?.action) {
        case "company-delete":
          deleteCompany({
            companyId: String(obj?.parameters?.id),
          });
          break;
        case "company-unlink":
          if (tenantId) {
            removeCompanyFromTenant({
              tenantId,
              companyId: obj?.parameters?.id,
            });
          }
          break;
        default:
          break;
      }
    }
    setShowConfirm(false);
  };

  const menuClick = (action: SubsidiaryAction, row: CompanyRow) => {
    switch (action) {
      case "company-delete":
        setConfirmMessage(gettext("Do you want to delete the company?"));
        handleOnClickRemoveLink(action, row?.id);
        break;
      case "company-unlink":
        setConfirmMessage(gettext("Do you want to unlink the company?"));
        handleOnClickRemoveLink(action, row?.id);
        break;
      case "company-edit":
        if (parmsTenantId) {
          navigate(
            `/${parentPath}/tenants/${parmsTenantId}/companies/${customerId}/child-companies/edit/${row?.id}`,
            {
              state: {
                companyName: row?.name,
                parentPath: location.pathname,
                company: row,
              },
            },
          );
        } else {
          navigate(
            `/${parentPath}/companies/${customerId}/child-companies/edit/${row?.id}`,
            {
              state: {
                companyName: row?.name,
                parentPath: location.pathname,
                company: row,
              },
            },
          );
        }
        break;
      case "company-locations":
        if (parmsTenantId) {
          navigate(
            `/${parentPath}/tenants/${parmsTenantId}/companies/${customerId}/child-companies/${row?.id}/locations`,
            {
              state: { companyName: row?.name, parentPath: location.pathname },
            },
          );
        } else {
          navigate(
            `/${parentPath}/companies/${customerId}/child-companies/${row?.id}/locations`,
            {
              state: { companyName: row?.name, parentPath: location.pathname },
            },
          );
        }
        break;
      default:
        break;
    }
  };

  return (
    <>
      <TableView1
        title={gettext("Child Companies")}
        rows={subsidiaries || []}
        columns={columns}
        loading={loading}
        initialState={initialState}
        postToolbar={
          <Stack
            direction="row"
            spacing={1}
            sx={{ pl: 1, alignItems: "center" }}
          >
            {!!backLink && <BackButton link={`${backLink}`} />}
            {showItems.showAdd && (
              <Button
                variant="contained"
                component={Link}
                to={
                  parmsTenantId
                    ? `/${parentPath}/tenants/${parmsTenantId}/companies/${customerId}/child-companies/add`
                    : `/${parentPath}/companies/${customerId}/child-companies/add`
                }
                state={{
                  parentPath: location.pathname,
                  parentCustomerId: customerId,
                  customerType: "child",
                }}
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {gettext("Add child company")}
              </Button>
            )}
          </Stack>
        }
        showGridToolbar
        showGridToolbarExport={!!props.showExport}
        showGridToolbarQuickFilter
        beforeTable={
          <>
            <br />
          </>
        }
      />

      <ConfirmNotification
        isOpen={showConfirm}
        title={gettext("Delete Company")}
        parameters={dialogParameters}
        getResult={handleGetResult}
        message={confirmMessage}
      />
    </>
  );
};

SubsidiaryList.propTypes = {
  parentPath: string,
  customerId: string,
  showAdd: bool,
  showExport: bool,
  showEdit: bool,
  showDelete: bool,
  showUnlink: bool,
  showLocations: bool,
};
